import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { bibliographyColumnsVisibility } from "@/config/dashboard/bibliography";
import { exemplairesColumnsVisibility } from "@/config/dashboard/exemplaires";
import { FormLayoutElement } from "@/config/forms/general";
import { SupportedLocale } from "@/i18n";
import { TypeDefaultImage } from "@/lib/image";
import {
  Bibliography,
  MultipleSampleInBibliography,
  Multiplesample,
} from "@/lib/types";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkDrawerRowActions } from "@/components/table/LinkDrawerRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import {
  BibliographyLinkGridCard,
  MultiplesampleLinkGridCard,
} from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";
import MultiplesampleInBibliographyDrawer from "./drawer/MultiplesampleInBibliographyDrawer";
import { formatMultiplesampleAuthorDisplay } from "./multiplesampleAuthorDisplay";

interface ComplexLinkProps {
  id: string;
  multiplesample: Multiplesample | undefined;
  bibliography: Bibliography | undefined;
  type: LinkableType;
  fields: FormLayoutElement[] | undefined;
}

type MultiplesampleToBibliographyProps =
  LinksTableProps<MultipleSampleInBibliography> & {
    multiplesample: Multiplesample | undefined;
  };

type BibliographyToMultiplesampleProps =
  LinksTableProps<MultipleSampleInBibliography> & {
    bibliography: Bibliography | undefined;
    type: LinkableType;
  };

const MultiplesampleInBibliographyLinks = ({
  id,
  multiplesample,
  bibliography,
  type,
  fields,
}: ComplexLinkProps) => {
  const { t, i18n } = useTranslation();
  const { data: complexRelation } = useGetComplexRelationFields(
    id,
    "multipleSampleInBibliography",
    type,
  );
  const { mutate: deleteLink } = useDeleteComplexLink(
    id,
    "multipleSampleInBibliography",
    type,
  );

  const [searchContent, setSearchContent] = useState<string>("");
  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (linkId: string) => {
      deleteLink({
        id: linkId,
        relation: "multipleSampleInBibliography",
      });
    },
    [deleteLink],
  );

  const relatedData = useMemo(
    () => (Array.isArray(complexRelation) ? complexRelation : []),
    [complexRelation],
  );

  const renderMultipleSampleInBibliographyRowActions = useCallback(
    (data: MultipleSampleInBibliography) => {
          const canEdit =
            data.bibliography != null && data.multiplesample != null;
          const preview =
            type === "bibliography" && data.multiplesample
              ? {
                  previewType: "multiplesample" as TypeDefaultImage,
                  previewId: data.multiplesample.id,
                  previewElements: [
                    {
                      label: t("exemplaireDansEstampesMultiplesDescriptionNumero"),
                      value:
                        data.multiplesample
                          .exemplaireDansEstampesMultiplesDescriptionNumero,
                      type: "text" as const,
                    },
                    {
                      label: t("exemplaireDansEstampesMultiplesDescriptionNumeroInventaire"),
                      value:
                        data.multiplesample
                          .exemplaireDansEstampesMultiplesDescriptionNumeroInventaire,
                      type: "text" as const,
                    },
                    {
                      label: t("exemplaireDansEstampesMultiplesDescriptionLocalisation"),
                      value:
                        data.multiplesample
                          .exemplaireDansEstampesMultiplesDescriptionLocalisation,
                      type: "text" as const,
                    },
                    {
                      label: t("exemplaireDansEstampesMultiplesDescriptionNotes"),
                      value:
                        data.multiplesample
                          .exemplaireDansEstampesMultiplesDescriptionNotes,
                      type: "richtext" as const,
                    },
                    {
                      label: t("attachments"),
                      value: data.multiplesample.attachments ?? [],
                      type: "image" as const,
                    },
                  ],
                }
              : type === "multiplesample" && data.bibliography
                ? {
                    previewType: "bibliography" as const,
                    previewId: data.bibliography.id,
                    previewElements: [
                      {
                        label: t("bibliographieDescriptionNumeroBibliographie"),
                        value:
                          data.bibliography
                            .bibliographieDescriptionNumeroBibliographie,
                        type: "text" as const,
                      },
                      {
                        label: t("bibliographieDescriptionResumeLong"),
                        value: data.bibliography.bibliographieDescriptionResumeLong,
                        type: "richtext" as const,
                      },
                      {
                        label: t("attachments"),
                        value: data.bibliography.attachments ?? [],
                        type: "image" as const,
                      },
                    ],
                  }
                : null;

          if (!preview) return null;

          return (
            <LinkDrawerRowActions
              previewType={preview.previewType}
              previewId={preview.previewId}
              previewElements={preview.previewElements}
              onDelete={() => handleDeleteRelation(data.id)}
              editDrawer={
                canEdit
                  ? ({ open, onOpenChange }) => (
                      <MultiplesampleInBibliographyDrawer
                        fields={fields}
                        data={data}
                        queryId={id}
                        type={type}
                        open={open}
                        onOpenChange={onOpenChange}
                      />
                    )
                  : undefined
              }
            />
          );
    },
    [t, type, fields, handleDeleteRelation, id],
  );

  const columns: ColumnDef<MultipleSampleInBibliography, any>[] = useMemo(
    () => [
      ...(type === "multiplesample"
        ? [
            {
              accessorKey:
                "bibliographieDescriptionNumeroBibliographie",
              header: t("bibliographieDescriptionNumeroBibliographie"),
              size: 100,
              cell: ({ row }: { row: Row<MultipleSampleInBibliography> }) => (
                <TextCell
                  value={
                    row.original.bibliography
                      ?.bibliographieDescriptionNumeroBibliographie
                  }
                />
              ),
            },
            {
              accessorKey: "vignette",
              header: "",
              size: 100,
              cell: ({ row }: { row: Row<MultipleSampleInBibliography> }) => (
                <ImageCell
                  value=""
                  attachments={
                    row.original.bibliography?.attachments ?? []
                  }
                  domain="bibliography"
                />
              ),
            },
            {
              accessorKey: "bibliographieDescriptionTitre",
              header: t("bibliographieDescriptionTitre"),
              size: 150,
              cell: ({ row }: { row: Row<MultipleSampleInBibliography> }) => (
                <TextCell
                  value={
                    row.original.bibliography?.bibliographieDescriptionTitre
                  }
                />
              ),
            },
            {
              accessorKey: "bibliographieDescriptionAnnee",
              header: t("bibliographieDescriptionAnnee"),
              size: 50,
              cell: ({ row }: { row: Row<MultipleSampleInBibliography> }) => (
                <TextCell
                  value={
                    row.original.bibliography?.bibliographieDescriptionAnnee
                  }
                />
              ),
            },
            {
              accessorKey: "bibliographieDescriptionAuteur",
              header: t("bibliographieDescriptionAuteur"),
              size: 150,
              cell: ({ row }: { row: Row<MultipleSampleInBibliography> }) => (
                <TextCell
                  value={
                    row.original.bibliography?.bibliographieDescriptionAuteur
                  }
                />
              ),
            },
          ]
        : type === "bibliography"
          ? [
              {
                accessorKey:
                  "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
                header: t(
                  "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
                ),
                size: 100,
                cell: ({
                  row,
                }: {
                  row: Row<MultipleSampleInBibliography>;
                }) => (
                  <TextCell
                    value={
                      row.original.multiplesample
                        ?.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement
                    }
                  />
                ),
              },
              {
                accessorKey: "vignette",
                header: "",
                size: 100,
                cell: ({
                  row,
                }: {
                  row: Row<MultipleSampleInBibliography>;
                }) => (
                  <ImageCell
                    value=""
                    attachments={
                      row.original.multiplesample?.attachments ?? []
                    }
                    domain="multiplesample"
                  />
                ),
              },
              {
                accessorKey:
                  "exemplaireDansEstampesMultiplesDescriptionNumeroInventaire",
                header: t(
                  "exemplaireDansEstampesMultiplesDescriptionNumeroInventaire",
                ),
                size: 100,
                cell: ({
                  row,
                }: {
                  row: Row<MultipleSampleInBibliography>;
                }                ) => (
                  <TextCell
                    value={
                      row.original.multiplesample
                        ?.exemplaireDansEstampesMultiplesDescriptionNumeroInventaire
                    }
                  />
                ),
              },
              {
                accessorKey: "author",
                header: t("tab_relatedAuthor"),
                size: 160,
                cell: ({
                  row,
                }: {
                  row: Row<MultipleSampleInBibliography>;
                }) => (
                  <TextCell
                    value={formatMultiplesampleAuthorDisplay(
                      row.original.multiplesample,
                      i18n.language as SupportedLocale,
                    )}
                  />
                ),
              },
              {
                accessorKey: "exemplaireDansEstampesMultiplesDescriptionLocalisation",
                header: t("exemplaireDansEstampesMultiplesDescriptionLocalisation"),
                size: 100,
                cell: ({
                  row,
                }: {
                  row: Row<MultipleSampleInBibliography>;
                }) => (
                  <TextCell
                    value={
                      row.original.multiplesample
                        ?.exemplaireDansEstampesMultiplesDescriptionLocalisation
                    }
                  />
                ),
              },
              {
                accessorKey: "exemplaireDansEstampesMultiplesDescriptionNotes",
                header: t("exemplaireDansEstampesMultiplesDescriptionNotes"),
                size: 120,
                cell: ({
                  row,
                }: {
                  row: Row<MultipleSampleInBibliography>;
                }) => (
                  <HtmlCell
                    value={
                      row.original.multiplesample
                        ?.exemplaireDansEstampesMultiplesDescriptionNotes ?? ""
                    }
                  />
                ),
              },
            ]
          : []),
      {
        accessorKey: "multiplesampleDansBibliographieDescriptionReproduitPage",
        header: t("multiplesampleDansBibliographieDescriptionReproduitPage"),
        size: 50,
        cell: ({ row }: { row: Row<MultipleSampleInBibliography> }) => (
          <TextCell
            value={
              row.original
                .multiplesampleDansBibliographieDescriptionReproduitPage
            }
          />
        ),
      },
      {
        accessorKey: "multiplesampleDansBibliographieDescriptionCitePage",
        header: t("multiplesampleDansBibliographieDescriptionCitePage"),
        size: 50,
        cell: ({ row }: { row: Row<MultipleSampleInBibliography> }) => (
          <TextCell
            value={
              row.original.multiplesampleDansBibliographieDescriptionCitePage
            }
          />
        ),
      },
      {
        accessorKey: "multiplesampleDansBibliographieDescriptionPhotographe",
        header: t("multiplesampleDansBibliographieDescriptionPhotographe"),
        size: 80,
        cell: ({ row }: { row: Row<MultipleSampleInBibliography> }) => (
          <TextCell
            value={
              row.original
                .multiplesampleDansBibliographieDescriptionPhotographe ?? ""
            }
          />
        ),
      },
      {
        accessorKey:
          "multiplesampleDansBibliographieDescriptionReproductionCouleur",
        header: t(
          "multiplesampleDansBibliographieDescriptionReproductionCouleur",
        ),
        size: 50,
        cell: ({ row }: { row: Row<MultipleSampleInBibliography> }) => (
          <TextCell
            value={
              row.original
                .multiplesampleDansBibliographieDescriptionReproductionCouleur
                ? t("yes")
                : t("no")
            }
          />
        ),
      },
      {
        accessorKey: "multiplesampleDansBibliographieDescriptionNonPagine",
        header: t("multiplesampleDansBibliographieDescriptionNonPagine"),
        size: 50,
        cell: ({ row }: { row: Row<MultipleSampleInBibliography> }) => (
          <TextCell
            value={
              row.original.multiplesampleDansBibliographieDescriptionNonPagine
                ? t("yes")
                : t("no")
            }
          />
        ),
      },
      {
        accessorKey: "multiplesampleDansBibliographieNotes",
        header: t("multiplesampleDansBibliographieNotes"),
        cell: ({ row }: { row: Row<MultipleSampleInBibliography> }) => (
          <HtmlCell
            value={row.original.multiplesampleDansBibliographieNotes ?? ""}
          />
        ),
      },
      createActionsColumnDef<MultipleSampleInBibliography>(({ row }) =>
        renderMultipleSampleInBibliographyRowActions(row.original),
      ),
    ],
    [fields, handleDeleteRelation, id, i18n.language, t, type],
  );

  const datatableConf = useMemo(() => {
    if (type === "multiplesample") {
      return (
        <MultiplesampleToBibliography
          id={id}
          relatedData={relatedData}
          fields={fields}
          multiplesample={multiplesample}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderMultipleSampleInBibliographyRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("bibliographiesRelated")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      );
    }
    return (
      <BibliographyToMultiplesample
        id={id}
        type={type}
        relatedData={relatedData}
        fields={fields}
        bibliography={bibliography}
        isAddMode={isAddMode}
        columnsLink={columns}
        renderRowActions={renderMultipleSampleInBibliographyRowActions}
        searchContent={searchContent}
        searchInput={
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        }
        listGridTitle={t("multiplesamplesRelated")}
        layoutBackLabel={t("relatedRecords")}
        setIsAddMode={setIsAddMode}
      />
    );
  }, [
    type,
    id,
    relatedData,
    fields,
    bibliography,
    isAddMode,
    columns,
    searchContent,
    multiplesample,
    t,
  ]);

  return <div className="w-full">{datatableConf}</div>;
};

const MultiplesampleToBibliography = ({
  id,
  relatedData,
  fields,
  multiplesample,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: MultiplesampleToBibliographyProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "multiplesample-links-bibliography-add";
  const STORAGE_KEY_LINKED = "multiplesample-links-bibliography-linked";
  const defaultVisibility = { ...bibliographyColumnsVisibility, actions: true };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notRelatedBibliographies } =
    useGetNotRelatedResource<Bibliography>({
      resourceId: id,
      resultType: "bibliography",
      searchQuery: searchContent,
      pagination: getPagination,
    });

  const columnsBibliographies: ColumnDef<Bibliography, any>[] = useMemo(
    () => [
      {
        accessorKey: "bibliographieDescriptionNumeroBibliographie",
        header: t("bibliographieDescriptionNumeroBibliographie"),
        enableResizing: true,
        cell: ({ row }: { row: Row<Bibliography> }) => (
          <div className="flex items-center gap-2 text-nowrap">
            <p className="text-[14px] font-[500]">
              {row.original.bibliographieDescriptionNumeroBibliographie}
            </p>
          </div>
        ),
      },
      {
        accessorKey: "bibliographieDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Bibliography> }) => (
          <ImageCell
            value=""
            attachments={row.original.attachments ?? []}
            domain="bibliography"
          />
        ),
      },
      {
        accessorKey: "bibliographieDescriptionTitre",
        header: t("bibliographieDescriptionTitre"),
        size: 100,
        cell: (props: { getValue: () => unknown }) => (
          <TextCell value={props.getValue() as string} />
        ),
      },
      {
        accessorKey: "bibliographieDescriptionAnnee",
        header: t("bibliographieDescriptionAnnee"),
        size: 100,
        cell: ({ row }: { row: Row<Bibliography> }) => (
          <TextCell value={row.original.bibliographieDescriptionAnnee} />
        ),
      },
      {
        accessorKey: "bibliographieDescriptionAuteur",
        header: t("bibliographieDescriptionAuteur"),
        size: 200,
        cell: (props: { getValue: () => unknown }) => (
          <TextCell value={props.getValue() as string} />
        ),
      },
      createActionsColumnDef<Bibliography>(({ row }: { row: Row<Bibliography> }) => {
          if (!multiplesample) return null;
          return (
            <LinkDrawerRowActions
              previewType="bibliography"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("bibliographieDescriptionNumeroBibliographie"),
                  value: row.original.bibliographieDescriptionNumeroBibliographie,
                  type: "text",
                },
                {
                  label: t("bibliographieDescriptionResumeLong"),
                  value: row.original.bibliographieDescriptionResumeLong,
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: row.original.attachments,
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <MultiplesampleInBibliographyDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={id}
                  type="multiplesample"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    multiplesample,
                    bibliography: row.original,
                    multiplesampleDansBibliographieDescriptionReproduitPage: "",
                    multiplesampleDansBibliographieDescriptionReproduitPlanche: "",
                    multiplesampleDansBibliographieDescriptionPlancheNonNumerotee:
                      false,
                    multiplesampleDansBibliographieDescriptionCitePage: "",
                    multiplesampleDansBibliographieDescriptionListePage: "",
                    multiplesampleDansBibliographieDescriptionSousNumero: "",
                    multiplesampleDansBibliographieDescriptionPhotographe: "",
                    multiplesampleDansBibliographieDescriptionReproductionCouleur:
                      false,
                    multiSampleInBiblioDescReproCover: false,
                    multiplesampleDansBibliographieDescriptionNonPagine: false,
                    multiplesampleDansBibliographieDescriptionTexteLibre: "",
                    multiplesampleDansBibliographieNotes: "",
                  }}
                />
              )}
            />
          );
        }),
    ],
    [id, multiplesample, fields, t],
  );

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable columns={columnsBibliographies} data={notRelatedBibliographies?.data ?? []} pagination={{ pagination: getPagination, setPage: setPagination, totalSize: notRelatedBibliographies?.totalSize ?? 0 }} storageKey={STORAGE_KEY_ADD} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityAdd, onColumnOrderChange: setColumnsOrderAdd, state: { columnVisibility: columnVisibilityAdd, columnOrder: columnsOrderAdd } }} />
  );
  const listTable = (
    <DataTable columns={columnsLink} data={paginatedRelatedData} pagination={relatedPaginationProps} storageKey={STORAGE_KEY_LINKED} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityLinked, onColumnOrderChange: setColumnsOrderLinked, state: { columnVisibility: columnVisibilityLinked, columnOrder: columnsOrderLinked } }} />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <BibliographyLinkGridCard
                  item={row.bibliography}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <MultiplesampleInBibliographyDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="multiplesample"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

const BibliographyToMultiplesample = ({
  id,
  type,
  relatedData,
  fields,
  bibliography,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: BibliographyToMultiplesampleProps) => {
  const { t, i18n } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD_MS = "bibliography-links-multiplesample-add";
  const STORAGE_KEY_LINKED_MS = "bibliography-links-multiplesample-linked";
  const defaultVisibilityMS = {
    ...exemplairesColumnsVisibility,
    author: true,
    actions: true,
  };
  const [columnVisibilityAddMS, setColumnVisibilityAddMS] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD_MS);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityMS, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityMS };
    });
  const [columnVisibilityLinkedMS, setColumnVisibilityLinkedMS] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_MS);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityMS, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityMS };
    });
  const [columnsOrderAddMS, setColumnsOrderAddMS] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD_MS);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinkedMS, setColumnsOrderLinkedMS] = useState<string[]>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_MS);
        if (saved) {
          const { order, columnOrder } = saved;
          const storedOrder = Array.isArray(order)
            ? order
            : Array.isArray(columnOrder)
              ? columnOrder
              : null;
          if (Array.isArray(storedOrder)) {
            const normalizedOrder = [...new Set(storedOrder)] as string[];
            return normalizedOrder.includes("actions")
              ? [
                  ...normalizedOrder.filter((id) => id !== "actions"),
                  "actions",
                ]
              : normalizedOrder;
          }
        }
      } catch {
        // ignore
      }
      return [];
    },
  );

  const { data: notAlreadyRelatedMultiplesamples } =
    useGetNotRelatedResource<Multiplesample>({
      resultType: "multiplesample",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
      anchorResourceType: type,
    });

  const columnsMultiplesamples: ColumnDef<Multiplesample, any>[] = useMemo(
    () => [
      {
        accessorKey:
          "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
        header: t(
          "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
        ),
        cell: ({ row }: { row: Row<Multiplesample> }) => (
          <TextCell
            value={
              row.original
                .exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement
            }
          />
        ),
      },
      {
        accessorKey: "exemplaireDansEstampesMultiplesDescriptionNumero",
        header: t("exemplaireDansEstampesMultiplesDescriptionNumero"),
        cell: ({ row }: { row: Row<Multiplesample> }) => (
          <TextCell
            value={
              row.original.exemplaireDansEstampesMultiplesDescriptionNumero
            }
          />
        ),
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Multiplesample> }) => (
          <ImageCell
            value=""
            attachments={row.original.attachments ?? []}
            domain="multiplesample"
          />
        ),
      },
      {
        accessorKey:
          "exemplaireDansEstampesMultiplesDescriptionNumeroInventaire",
        header: t("exemplaireDansEstampesMultiplesDescriptionNumeroInventaire"),
        cell: ({ row }: { row: Row<Multiplesample> }) => (
          <TextCell
            value={
              row.original
                .exemplaireDansEstampesMultiplesDescriptionNumeroInventaire
            }
          />
        ),
      },
      {
        accessorKey: "author",
        header: t("tab_relatedAuthor"),
        cell: ({ row }: { row: Row<Multiplesample> }) => (
          <TextCell
            value={formatMultiplesampleAuthorDisplay(
              row.original,
              i18n.language as SupportedLocale,
            )}
          />
        ),
      },
      {
        accessorKey: "exemplaireDansEstampesMultiplesDescriptionLocalisation",
        header: t("exemplaireDansEstampesMultiplesDescriptionLocalisation"),
        cell: ({ row }: { row: Row<Multiplesample> }) => (
          <TextCell
            value={
              row.original
                .exemplaireDansEstampesMultiplesDescriptionLocalisation
            }
          />
        ),
      },
      createActionsColumnDef<Multiplesample>(({ row }: { row: Row<Multiplesample> }) => {
          if (!bibliography) return null;
          return (
            <LinkDrawerRowActions
              previewType={"multiplesample" as TypeDefaultImage}
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("exemplaireDansEstampesMultiplesDescriptionNumero"),
                  value:
                    row.original.exemplaireDansEstampesMultiplesDescriptionNumero,
                  type: "text",
                },
                {
                  label: t("exemplaireDansEstampesMultiplesDescriptionNumeroInventaire"),
                  value:
                    row.original
                      .exemplaireDansEstampesMultiplesDescriptionNumeroInventaire,
                  type: "text",
                },
                {
                  label: t("exemplaireDansEstampesMultiplesDescriptionLocalisation"),
                  value:
                    row.original
                      .exemplaireDansEstampesMultiplesDescriptionLocalisation,
                  type: "text",
                },
                {
                  label: t("exemplaireDansEstampesMultiplesDescriptionNotes"),
                  value:
                    row.original.exemplaireDansEstampesMultiplesDescriptionNotes,
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: row.original.attachments ?? [],
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <MultiplesampleInBibliographyDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={id}
                  type="bibliography"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    multiplesample: row.original,
                    bibliography,
                    multiplesampleDansBibliographieDescriptionReproduitPage: "",
                    multiplesampleDansBibliographieDescriptionReproduitPlanche: "",
                    multiplesampleDansBibliographieDescriptionPlancheNonNumerotee:
                      false,
                    multiplesampleDansBibliographieDescriptionCitePage: "",
                    multiplesampleDansBibliographieDescriptionListePage: "",
                    multiplesampleDansBibliographieDescriptionSousNumero: "",
                    multiplesampleDansBibliographieDescriptionPhotographe: "",
                    multiplesampleDansBibliographieDescriptionReproductionCouleur:
                      false,
                    multiSampleInBiblioDescReproCover: false,
                    multiplesampleDansBibliographieDescriptionNonPagine: false,
                    multiplesampleDansBibliographieDescriptionTexteLibre: "",
                    multiplesampleDansBibliographieNotes: "",
                  }}
                />
              )}
            />
          );
        }),
    ],
    [id, bibliography, fields, i18n.language, t],
  );

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable columns={columnsMultiplesamples} data={notAlreadyRelatedMultiplesamples?.data ?? []} pagination={{ pagination: getPagination, setPage: setPagination, totalSize: notAlreadyRelatedMultiplesamples?.totalSize ?? 0 }} storageKey={STORAGE_KEY_ADD_MS} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityAddMS, onColumnOrderChange: setColumnsOrderAddMS, state: { columnVisibility: columnVisibilityAddMS, columnOrder: columnsOrderAddMS } }} />
  );
  const listTable = (
    <DataTable columns={columnsLink} data={paginatedRelatedData} pagination={relatedPaginationProps} storageKey={STORAGE_KEY_LINKED_MS} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityLinkedMS, onColumnOrderChange: setColumnsOrderLinkedMS, state: { columnVisibility: columnVisibilityLinkedMS, columnOrder: columnsOrderLinkedMS } }} />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED_MS}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <MultiplesampleLinkGridCard
                  item={row.multiplesample}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <MultiplesampleInBibliographyDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="bibliography"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

export default MultiplesampleInBibliographyLinks;
